import { BannerId } from '../../typings/banners';
import { EditBannersSessionValue } from '../../typings/bannersEditState';
import { EditCampaignSessionValue } from '../../typings/campaignsEdit';
import { ColumnsSettingsViewDict, SessionDict } from '../../typings/common';
import { AdditionalFiltersDict } from '../../typings/domain';
import { GridSelectionInfo } from '../../typings/grid';
import { EditGroupSessionValue } from '../../typings/groupsEditState';

export const gridStorageKey = 'dna';

export enum StorageKeyType {
    columnsSettings = 'columnsSettings',
    goalsSettings = 'goalsSettings',
    logColumnsSettings = 'logColumnsSettings',
    recommendationSettings = 'recommendationSettings',
    minusPhraseLibrarySettings = 'minusPhraseLibrarySettings',
    session = 'session',
    dealsColumnsSettings = 'dealsColumnsSettings',
    selectionInfo = 'selectionInfo',
    csrfToken = 'csrfToken',
    reportsSettings = 'reportsSettings',
    reportsDomainSettings = 'reportsDomainSettings',
    recommendationTooltipHidden = 'recommendationTooltipHidden',
    additionalFilter = 'additionalFilter',
    isGridTotalBoxVisible = 'isGridTotalBoxVisible',
    isCompactViewEnabled = 'isCompactViewEnabled',
    newInterfacePromoShowTime = 'newInterfacePromoShowTime',
    timestampLastViewNotificationError = 'timestampLastViewNotificationError',
    bannersIds = 'bannersIds',
    campaignsIds = 'campaignsIds',
    editCampaigns = 'editCampaigns',
    editGroups = 'editGroups',
    editBanners = 'editBanners',
    isOnlyFavoriteCampaignsVisible = 'isOnlyFavoriteCampaignsVisible'
}

export interface StorageData {
    [StorageKeyType.columnsSettings]: ColumnsSettingsViewDict;
    [StorageKeyType.logColumnsSettings]: ColumnsSettingsViewDict;
    [StorageKeyType.reportsSettings]: ColumnsSettingsViewDict;
    [StorageKeyType.reportsDomainSettings]: ColumnsSettingsViewDict;
    [StorageKeyType.recommendationSettings]: ColumnsSettingsViewDict;
    [StorageKeyType.dealsColumnsSettings]: ColumnsSettingsViewDict;
    [StorageKeyType.minusPhraseLibrarySettings]: ColumnsSettingsViewDict;
    [StorageKeyType.session]: SessionDict;
    [StorageKeyType.selectionInfo]?: GridSelectionInfo;
    [StorageKeyType.csrfToken]?: string;
    [StorageKeyType.additionalFilter]?: AdditionalFiltersDict;
    [StorageKeyType.isGridTotalBoxVisible]?: boolean;
    [StorageKeyType.isCompactViewEnabled]?: boolean;
    [StorageKeyType.newInterfacePromoShowTime]?: number;
    [StorageKeyType.timestampLastViewNotificationError]?: number;
    [StorageKeyType.bannersIds]?: Array<BannerId>;
    [StorageKeyType.campaignsIds]?: Array<string>;
    [StorageKeyType.editCampaigns]?: EditCampaignSessionValue;
    [StorageKeyType.editGroups]?: EditGroupSessionValue;
    [StorageKeyType.editBanners]?: EditBannersSessionValue;
    [StorageKeyType.isOnlyFavoriteCampaignsVisible]?: boolean;
    [StorageKeyType.recommendationTooltipHidden]?: boolean;
}

export enum StorageType {
    session = 'session',
    local = 'local'
}

export class Storage<K extends keyof StorageData> {
    private storageSource:
        | WindowLocalStorage['localStorage']
        | WindowSessionStorage['sessionStorage'];

    constructor(storageType: StorageType) {
        if (storageType === StorageType.session) {
            this.storageSource = window.sessionStorage;
            return;
        }

        if (storageType === StorageType.local) {
            this.storageSource = window.localStorage;
            return;
        }
    }

    public setItem(key: K, rawValue: StorageData[K]): void {
        const currentStorageValue = this.getCurrentStorageValue();

        currentStorageValue[key] = rawValue;

        return this.updateStorageValue(currentStorageValue);
    }

    public setItemsFromObj(obj: Partial<StorageData>): void {
        Object.keys(obj).forEach(key => {
            if (obj[key] !== undefined) {
                this.setItem(StorageKeyType[key], obj[key]);
            }
        });
    }

    public getItem(key: K): StorageData[K] {
        return this.getCurrentStorageValue()[key];
    }

    public removeItem(key: K): void {
        const currentStorageValue = this.getCurrentStorageValue();
        delete currentStorageValue[key];

        return this.updateStorageValue(currentStorageValue);
    }

    public clear(): void {
        return this.storageSource.removeItem(gridStorageKey);
    }

    private updateStorageValue(value: Object): void {
        // в каких-то случаях на винде 10 в хроме 60 на главной странице и странице welcome localstorage равен null, возможно, это какие-то боты
        // данное решение помогает избежать падения в таких случаях
        try {
            this.storageSource.setItem(gridStorageKey, JSON.stringify(value));
        } catch (e) {
            return;
        }
    }

    private getCurrentStorageValue(): StorageData {
        // в каких-то случаях на винде 10 в хроме 60 на главной странице и странице welcome localstorage равен null, возможно, это какие-то боты
        // данное решение помогает избежать падения в таких случаях
        try {
            const value = this.storageSource.getItem(gridStorageKey);
            return value === null ? {} : JSON.parse(value);
        } catch (e) {
            return {
                [StorageKeyType.columnsSettings]: {},
                [StorageKeyType.logColumnsSettings]: {},
                [StorageKeyType.reportsSettings]: {},
                [StorageKeyType.reportsDomainSettings]: {},
                [StorageKeyType.recommendationSettings]: {},
                [StorageKeyType.dealsColumnsSettings]: {},
                [StorageKeyType.session]: {},
                [StorageKeyType.minusPhraseLibrarySettings]: {}
            };
        }
    }
}

export const SessionStorage = new Storage(StorageType.session);
export const LocalStorage = new Storage(StorageType.local);
